import React from "react";
import { sumNumbers } from "../Projects";
import { A4Estimate, Extras } from "../Quote";

export const ProjectNumbers = ({
  allMaterials,
  project,
  team,
  design,
  materialKeyMap,
}) => {
  if (!team || !team.tierLimits.seePrices || !allMaterials) return null;

  const totalLength = allMaterials.totalLength || 0;

  // Calculate total materials cost
  const totalMaterialsCost = Object.entries(allMaterials)
    .filter(
      ([key]) =>
        ![
          "singleGates",
          "doubleGates",
          "gateExtensions",
          "totalLength",
        ].includes(key),
    )
    .reduce((sum, [materialKey, quantity]) => {
      const designMaterialKey = materialKeyMap[materialKey];
      const material = design.materials[designMaterialKey];
      if (!material || !material.included) return sum;

      // For mesh, use totalLength instead of quantity for price calculation
      const itemTotal =
        materialKey === "mesh"
          ? totalLength * material.price
          : quantity * material.price;

      return sum + itemTotal;
    }, 0);

  // Add gate materials cost
  let gateCost = 0;
  if (design.gate?.gate?.included) {
    gateCost +=
      ((allMaterials.singleGates || 0) + (allMaterials.doubleGates || 0) * 2) *
      design.gate.gate.price;
  }
  if (allMaterials.singleGates > 0 && design.gate?.singleGateKit?.included) {
    gateCost += allMaterials.singleGates * design.gate.singleGateKit.price;
  }
  if (allMaterials.doubleGates > 0 && design.gate?.doubleGateKit?.included) {
    gateCost += allMaterials.doubleGates * design.gate.doubleGateKit.price;
  }
  if (allMaterials.gateExtensions > 0 && design.gate?.gateExtension?.included) {
    gateCost += allMaterials.gateExtensions * design.gate.gateExtension.price;
  }

  const totalBeforeTax = totalMaterialsCost + gateCost;
  const materialPlusTax = (
    totalBeforeTax * parseFloat(team.team.materialTax / 100 + 1)
  ).toFixed(2);

  const adjustCost = parseFloat(project.adjustCost) || 0;
  const materialMultiplier = (
    materialPlusTax * parseFloat(team.team.materialMultiplier / 100 + 1)
  ).toFixed(2);

  const totalFeet = sumNumbers(Object.values(project.lengths));
  const totalLabour = (
    team?.team?.labourCostPerFtChainlink * totalFeet
  ).toFixed(2);

  const totalCost = (
    parseFloat(totalLabour) + parseFloat(materialMultiplier)
  ).toFixed(2);
  const totalClientCost = (
    totalCost * parseFloat(team.team.serviceTax / 100 + 1)
  ).toFixed(2);

  const pricePerFtBeforeTax = (adjustCost + totalCost / totalFeet).toFixed(2);
  const costAdjustment = (adjustCost * totalFeet).toFixed(2);

  return (
    <>
      <div className="my-4 p-4 border rounded-lg bg-gray-50">
        <h3 className="text-xl font-bold mb-3">Cost Summary</h3>
        {team.permissions.includes("seeNumbers") ? (
          <>
            <p className="text-gray-700">
              <span className="font-medium">Material Markup:</span> $
              {(
                parseFloat(totalCost) -
                parseFloat(totalLabour) -
                parseFloat(materialPlusTax)
              ).toFixed(2)}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Material Plus Markup:</span> $
              {materialMultiplier}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Labour Cost:</span> ${totalLabour}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Total Cost:</span> ${totalCost}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Cost Adjustment:</span> $
              {costAdjustment}
            </p>
          </>
        ) : null}
        <p className="text-lg font-bold text-blue-700 mt-2">
          Customer Cost including Tax: ${totalClientCost}
        </p>
        <p className="text-gray-700">${pricePerFtBeforeTax} plus tax per ft</p>
      </div>

      <Extras />

      <A4Estimate
        key={`${project.id}-estimate`}
        project={project}
        clientName={`${project.clientFirstName || ""} ${project.clientLastName || ""}`}
        address={project.address}
        city={project.city}
        state={project.state}
        notes={project.notes}
        lineItems={[
          {
            item: design?.name || "Chainlink Fence",
            ft: `${sumNumbers(Object.values(project.lengths))}ft`,
            quantity: sumNumbers(Object.values(project.lengths)),
            price: pricePerFtBeforeTax,
          },
          ...(project?.extras || []),
        ]}
        team={team}
        design={design}
        pricePerFtBeforeTax={pricePerFtBeforeTax}
      />
    </>
  );
};
